import { useState, useEffect } from "react";
import { useAuthState } from "react-firebase-hooks/auth";
import { auth, db } from "../services/firebase";
import { doc, onSnapshot } from "firebase/firestore";
import { signOut } from "firebase/auth";

export function useUserData() {
  const [authUser, loading] = useAuthState(auth);
  const [userData, setUserData] = useState(null);
  const [userLoading, setUserLoading] = useState(true);

  useEffect(() => {
    let unsubscribe;

    if (authUser) {
      // Set up real-time listener for user data changes
      const userDocRef = doc(db, "users", authUser.uid);
      unsubscribe = onSnapshot(userDocRef, (docSnap) => {
        try {
          if (docSnap.exists()) {
            const userDocData = docSnap.data();
            // Check if user is deactivated
            if (userDocData.isActive === false || userDocData.isActive === "false") {
              console.log("User account is deactivated, signing out");
              signOut(auth);
              setUserData(null);
              setUserLoading(false);
              return;
            }
            setUserData({
              uid: authUser.uid,
              email: authUser.email,
              displayName: authUser.displayName || userDocData.name,
              role: userDocData.role || "student", // Use role from database, fallback to student
              name: userDocData.name,
              profileCompleted: userDocData.profileCompleted || false,
              ...userDocData, // Include all other user data
            });
          } else {
            // User document doesn't exist, this user was deleted by admin
            console.log("User document not found, signing out deleted user");
            signOut(auth);
            setUserData(null);
          }
        } catch (error) {
          console.error("Error processing user data snapshot:", error);
          // Fallback to basic auth data on error
          setUserData({
            uid: authUser.uid,
            email: authUser.email,
            displayName: authUser.displayName,
            role: "student", // Default role on error
          });
        } finally {
          setUserLoading(false);
        }
      });
    } else {
      setUserData(null);
      setUserLoading(false);
    }

    // Cleanup function to unsubscribe from listener
    return () => {
      if (unsubscribe) {
        unsubscribe();
      }
    };
  }, [authUser]);

  return [userData, userLoading || loading];
}
